import React from 'react';
import { Head, Section, SectionHeader } from '@/components';
import { GENERAL_FAQ } from '@/data/constants';

/**
 * Studio page component.
 * Displays company information, process, and FAQ.
 *
 * @returns The studio page.
 */
const Studio: React.FC = () => {
  return (
    <>
      <Head
        title="About Us & Process | Kivosystems"
        description="How we work. Your partners in digital creation."
        canonicalUrl="https://www.kivosystems.com/studio"
        url="https://www.kivosystems.com/studio"
        image="/logo.svg"
        keywords="about us process, web design agency methodology, collaborative development, cyprus software consultancy, london digital agency"
      />

      <Section className="bg-obsidian">
        <SectionHeader label="About Us" title="Your digital partner." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="text-lg leading-relaxed text-gray-400">
            <p className="mb-6">
              Kivosystems is a premium software engineering consultancy operating between London and Cyprus. We founded the company on a simple belief: you shouldn't have to be a tech expert to have world-class technology.
            </p>
            <p>
              We see ourselves as your partners. We listen to your goals, understand your challenges, and then we build the digital tools you need to overcome them. Whether that's a simple portfolio or a complex business system, we treat your project with the care it deserves.
            </p>
          </div>
          <div className="text-lg leading-relaxed text-gray-400">
            <p className="mb-6">
              Our team consists of experienced designers and engineers who value clarity and quality. We remove the stress of "going digital" so you can focus on what you do best running your business.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-obsidian-50 border-y border-white/5">
        <SectionHeader label="Our Approach" title="Simple & Collaborative." />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { step: '01', title: 'Listen', desc: 'We start by understanding your business, your audience, and your specific goals.' },
            { step: '02', title: 'Plan', desc: 'We create a roadmap and design concept so you know exactly what to expect.' },
            { step: '03', title: 'Build', desc: 'We bring the design to life, keeping you updated with regular progress reports.' },
            { step: '04', title: 'Launch', desc: 'We handle the technical launch, testing everything to ensure a smooth start.' },
            { step: '05', title: 'Grow', desc: 'We stay by your side to help you maintain and improve your platform over time.' },
          ].map((item) => (
            <div key={item.step} className="p-6 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors">
              <span className="text-xs font-bold text-sapphire-500 mb-2 block">{item.step}</span>
              <h4 className="text-lg font-display mb-3 text-white">{item.title}</h4>
              <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-obsidian">
        <SectionHeader label="FAQ" title="Common questions." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {GENERAL_FAQ.map((faq, i) => (
            <div key={i}>
              <h4 className="text-lg font-bold mb-3 text-white">{faq.question}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
};

export default Studio;
